import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User } from '@supabase/supabase-js';
import { authService } from '../services/auth';
import './UserMenu.css';

interface UserMenuProps {
  user: User;
  onSignOut: () => void;
}

export const UserMenu: React.FC<UserMenuProps> = ({ user, onSignOut }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSignOut = async () => {
    try {
      await authService.signOut();
      onSignOut();
      setIsOpen(false);
    } catch (error: any) {
      console.error('Sign out error:', error);
    }
  };

  const getDisplayName = () => {
    // Supabase stores user metadata differently than Firebase
    const metadata = user.user_metadata;
    return metadata?.full_name || metadata?.name || user.email?.split('@')[0] || 'User';
  };

  const getInitials = () => {
    const name = getDisplayName();
    return name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getAvatarUrl = () => {
    // Supabase stores avatar in user_metadata
    return user.user_metadata?.avatar_url || user.user_metadata?.picture || null;
  };

  return (
    <div className="user-menu" ref={menuRef}>
      <button className="user-menu-trigger" onClick={() => setIsOpen(!isOpen)} aria-label="User menu">
        {getAvatarUrl() && !avatarError ? (
          <img 
            src={getAvatarUrl()!} 
            alt={getDisplayName()} 
            className="user-menu-avatar"
            onError={() => setAvatarError(true)}
            crossOrigin="anonymous"
          />
        ) : (
          <div className="user-menu-avatar-initials">{getInitials()}</div>
        )}
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={`user-menu-chevron ${isOpen ? 'open' : ''}`}>
          <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {isOpen && (
        <div className="user-menu-dropdown">
          <div className="user-menu-info">
            <div className="user-menu-name">{getDisplayName()}</div>
            <div className="user-menu-email">{user.email}</div>
          </div>
          <div className="user-menu-divider"></div>
          <Link to="/credits" className="user-menu-item" onClick={() => setIsOpen(false)}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 1V15M12 4H6C5.46957 4 4.96086 4.21071 4.58579 4.58579C4.21071 4.96086 4 5.46957 4 6C4 6.53043 4.21071 7.03914 4.58579 7.41421C4.96086 7.78929 5.46957 8 6 8H10C10.5304 8 11.0391 8.21071 11.4142 8.58579C11.7893 8.96086 12 9.46957 12 10C12 10.5304 11.7893 11.0391 11.4142 11.4142C11.0391 11.7893 10.5304 12 10 12H4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Credits & Usage</span>
          </Link>
          <Link to="/pricing" className="user-menu-item" onClick={() => setIsOpen(false)}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 4H14M2 8H14M2 12H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Upgrade Plan</span>
          </Link>
          <div className="user-menu-divider"></div>
          <button className="user-menu-item user-menu-signout" onClick={handleSignOut}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 14H3C2.44772 14 2 13.5523 2 13V3C2 2.44772 2.44772 2 3 2H6M10 11L14 7M14 7L10 3M14 7H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
};
